import {
  HelpCircle,
  Search,
  FileText,
  BookOpen,
  Star,
  Zap,
  Flame,
  Clock,
  Target,
  Coins,
  TrendingUp,
  CheckSquare,
  Edit3,
  ListOrdered,
  MousePointer,
  Award,
  Repeat2,
  ArrowLeftRight,
  Settings,
  SlidersHorizontal,
  Palette,
  Monitor,
  ClipboardList,
  BarChart2,
  CalendarDays,
  Gift,
  Heart,
  User,
} from "lucide-react";
import "../styles/helpPage.css";

function Stat({ icon, label }) {
  return (
    <span className="help-stat">
      {icon}
      {label}
    </span>
  );
}

function Steps({ steps }) {
  return (
    <ol className="help-steps">
      {steps.map((s, i) => (
        <li key={i}>
          <span className="help-step-num">{i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Tips({ items }) {
  return (
    <div className="help-tips">
      <p className="help-tips-heading">Tips</p>
      <ul>
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

export default function HelpPage() {
  return (
    <div className="help-page">
      <div className="help-container">

        {/* ── Hero ── */}
        <div className="help-hero">
          <div className="help-hero-icon">
            <HelpCircle size={38} color="#fff" />
          </div>
          <h1>How to Play</h1>
          <p className="help-hero-sub">
            Everything you need to know about each game mode and account settings —
            rules, scoring, and tips to maximise your score.
          </p>
        </div>

        {/* ── Quick-nav ── */}
        <div className="help-quicknav">
          <span className="help-quicknav-label">Games</span>
          <a href="#spot-word"     className="blue">  <Search size={13} />        Spot the Word</a>
          <a href="#mcq-quiz"      className="indigo"><FileText size={13} />       MCQ Quiz</a>
          <a href="#comprehension" className="violet"><BookOpen size={13} />       Comprehension</a>
          <a href="#book-comp"     className="violet"><BookOpen size={13} />       Book Comprehension</a>
          <a href="#vocab"         className="green"> <Star size={13} />           Vocab Matching</a>
          <a href="#synonyms"      className="teal">  <Repeat2 size={13} />       Synonyms</a>
          <a href="#antonyms"      className="orange"><ArrowLeftRight size={13} /> Antonyms</a>
          <a href="#ranked"        className="red">   <Flame size={13} />          Ranked Mode</a>
          <span className="help-quicknav-label">Progress</span>
          <a href="#my-results"    className="green"> <ClipboardList size={13} /> My Results</a>
          <a href="#my-stats"      className="indigo"><BarChart2 size={13} />     My Stats</a>
          <a href="#my-report"     className="teal">  <User size={13} />          My Report</a>
          <span className="help-quicknav-label">Rewards</span>
          <a href="#calendar"      className="orange"><CalendarDays size={13} />  Login Calendar</a>
          <a href="#owl-hatchery"  className="violet"><Gift size={13} />          Owl Hatchery</a>
          <a href="#my-collection" className="red">   <Heart size={13} />         My Collection</a>
          <span className="help-quicknav-label">Account</span>
          <a href="#preferences"   className="violet"><Settings size={13} />      Preferences</a>
          <a href="#theme"         className="indigo"><Palette size={13} />       Theme</a>
        </div>

        {/* ════════════════════════════════════════
            1. SPOT THE WORD
        ════════════════════════════════════════ */}
        <div className="help-card" id="spot-word">
          <div className="help-card-header">
            <div className="help-card-icon blue"><Search size={22} /></div>
            <div>
              <p className="help-card-title">Spot the Word</p>
              <p className="help-card-subtitle">Vocabulary · 60 seconds</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Clock size={13} />}       label="60 seconds" />
            <Stat icon={<Target size={13} />}      label="~5 target words" />
            <Stat icon={<Coins size={13} />}       label="0.5 coins per score point" />
          </div>
          <div className="help-card-body">
            <p>
              A reading passage is displayed on screen with several target words
              hidden inside. Your job is to find and click every target word before
              time runs out.
            </p>
            <Steps steps={[
              "Read the passage and the list of target words shown on screen.",
              "Click each target word every time it appears in the text.",
              "Keep going until all instances are found or the 60-second timer expires.",
            ]} />
            <p>
              <strong>Scoring</strong> — you earn <strong>+10 points</strong> for
              each correct click and lose <strong>2 points</strong> for each wrong
              click. When you find every word, a time bonus of{" "}
              <strong>+1 point per 5 seconds remaining</strong> is added on top.
            </p>
            <Tips items={[
              "Read the target words first so you know what to look for.",
              "Avoid random clicking — the -2 penalty adds up fast.",
              "Words may appear more than once; click every occurrence.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            2. MCQ QUIZ
        ════════════════════════════════════════ */}
        <div className="help-card" id="mcq-quiz">
          <div className="help-card-header">
            <div className="help-card-icon indigo"><FileText size={22} /></div>
            <div>
              <p className="help-card-title">MCQ Quiz</p>
              <p className="help-card-subtitle">Comprehension · 5 minutes</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Clock size={13} />}       label="5 minutes" />
            <Stat icon={<ListOrdered size={13} />} label="10 questions" />
            <Stat icon={<Target size={13} />}      label="10 marks" />
            <Stat icon={<Coins size={13} />}       label="0.5 coins per score point" />
          </div>
          <div className="help-card-body">
            <p>
              An AI-generated passage is shown alongside ten multiple-choice
              comprehension questions. Each question has four options (A–D) and
              exactly one correct answer.
            </p>
            <Steps steps={[
              "Read the passage carefully.",
              "Answer all 10 questions by selecting one option each.",
              "Submit before the 5-minute timer reaches zero.",
            ]} />
            <p>
              <strong>Scoring</strong> — <strong>10 points</strong> per correct
              answer. A small time bonus of{" "}
              <strong>+1 point per 20 seconds remaining</strong> rewards fast
              finishers. Results are auto-marked instantly; you can review every
              question with correct answers highlighted after submitting.
            </p>
            <Tips items={[
              "You don't lose points for wrong answers — always give an answer.",
              "Use the time bonus as motivation to answer confidently rather than deliberating too long.",
              "After the game, use the review screen to learn which questions you missed.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            3. COMPREHENSION
        ════════════════════════════════════════ */}
        <div className="help-card" id="comprehension">
          <div className="help-card-header">
            <div className="help-card-icon violet"><BookOpen size={22} /></div>
            <div>
              <p className="help-card-title">Comprehension</p>
              <p className="help-card-subtitle">Reading &amp; Writing · 10 minutes</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Clock size={13} />}       label="10 minutes" />
            <Stat icon={<Edit3 size={13} />}       label="6 written questions" />
            <Stat icon={<Target size={13} />}      label="Variable marks per question" />
            <Stat icon={<Coins size={13} />}       label="2 coins per mark awarded" />
          </div>
          <div className="help-card-body">
            <p>
              A longer AI-generated passage is shown with six open-ended written
              questions. Each question states how many marks it is worth. Your
              answers are marked by AI against a detailed mark scheme.
            </p>
            <Steps steps={[
              "Read the passage on the left panel.",
              "Write your answers in the text boxes on the right.",
              "Submit before 10 minutes are up — the game auto-submits when time expires.",
              "Wait a moment while AI marks your answers against the mark scheme.",
              "Review your feedback, mark breakdown, and model answers.",
            ]} />
            <p>
              <strong>Scoring</strong> — marks are awarded per marking point
              matched. A <strong>time bonus of up to 50%</strong> of your raw
              marks is added for finishing early. Coins are{" "}
              <strong>2 per mark</strong> awarded.
            </p>
            <Tips items={[
              "Read the mark allocation for each question — prioritise high-mark questions if time is tight.",
              "Use full sentences and refer back to the text to pick up more marking points.",
              "You still earn partial marks — even an incomplete answer is worth writing.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            4. BOOK COMPREHENSION
        ════════════════════════════════════════ */}
        <div className="help-card" id="book-comp">
          <div className="help-card-header">
            <div className="help-card-icon violet"><BookOpen size={22} /></div>
            <div>
              <p className="help-card-title">Book Comprehension</p>
              <p className="help-card-subtitle">Classic Literature · MCQ or Written</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<ListOrdered size={13} />} label="20 questions" />
            <Stat icon={<Coins size={13} />}       label="2 coins per mark" />
          </div>
          <div className="help-card-body">
            <p>
              Work through chapters of classic books. Before starting, choose a
              genre, book, and chapter from the selection page, then pick whether
              you want an MCQ test or a written-answer exam.
            </p>

            <div className="help-variants">
              <div className="help-variant-card">
                <p className="help-variant-label">
                  <CheckSquare size={15} /> MCQ Mode
                </p>
                <p className="help-variant-detail">
                  20 multiple-choice questions · 1 mark each · 20-minute timer ·
                  auto-marked instantly
                </p>
              </div>
              <div className="help-variant-card">
                <p className="help-variant-label">
                  <Edit3 size={15} /> Written Mode
                </p>
                <p className="help-variant-detail">
                  20 written questions · up to 70 marks total · 45-minute timer ·
                  AI-marked with feedback
                </p>
              </div>
            </div>

            <Steps steps={[
              "On the Book Selector page, choose a genre, then a book, then a chapter.",
              "Select MCQ or Written Answer mode.",
              "Read the chapter passage displayed alongside the questions.",
              "Answer all questions and submit before the timer expires.",
            ]} />

            <p>
              <strong>Caching</strong> — question sets are generated by AI once
              per chapter and then saved. Returning to the same chapter loads the
              same questions instantly, so your score is comparable across attempts.
            </p>
            <Tips items={[
              "Written mode is worth far more marks — choose it when you have time to write detailed answers.",
              "In MCQ mode, keyboard shortcuts A/B/C/D let you select options quickly.",
              "Revisiting a chapter is a great way to improve your written score after reviewing the mark scheme.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            5. VOCAB MATCHING
        ════════════════════════════════════════ */}
        <div className="help-card" id="vocab">
          <div className="help-card-header">
            <div className="help-card-icon green"><Star size={22} /></div>
            <div>
              <p className="help-card-title">Vocab Matching</p>
              <p className="help-card-subtitle">Vocabulary · Continuous rounds</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<MousePointer size={13} />} label="4 words per round" />
            <Stat icon={<TrendingUp size={13} />}   label="ELO-scaled difficulty" />
            <Stat icon={<Coins size={13} />}         label="ELO multiplier on coins" />
          </div>
          <div className="help-card-body">
            <p>
              Four words and four definitions appear on screen. Drag or click to
              pair each word with its correct definition. The word pool is
              dynamically scaled to your ELO level, so difficulty grows as you
              improve.
            </p>
            <Steps steps={[
              "Four words and four definitions are shown — match each pair.",
              "The round is marked immediately; correct pairs turn green, wrong ones turn red.",
              "A new round loads automatically — keep going as long as you like.",
              "End the session whenever you choose to save your ELO and collect coins.",
            ]} />
            <p>
              <strong>Scoring</strong> — a correct match earns{" "}
              <strong>+1 point</strong> (or <strong>+10</strong> for a rare
              outlier word). A wrong match costs <strong>3–5 points</strong>.
              Coins per match scale with your current ELO (ELO ÷ 1000 multiplier),
              so higher-ranked players earn more per correct answer.
            </p>
            <Tips items={[
              "Take your time — there's no round timer, so precision beats speed.",
              "Outlier words (marked differently) are worth 10× — don't rush them.",
              "The longer your session, the more ELO is at stake — end on a good run.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            6. SYNONYMS
        ════════════════════════════════════════ */}
        <div className="help-card" id="synonyms">
          <div className="help-card-header">
            <div className="help-card-icon teal"><Repeat2 size={22} /></div>
            <div>
              <p className="help-card-title">Synonyms</p>
              <p className="help-card-subtitle">Vocabulary · Continuous rounds</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Repeat2 size={13} />}      label="4 options per question" />
            <Stat icon={<TrendingUp size={13} />}   label="ELO-scaled difficulty" />
            <Stat icon={<Coins size={13} />}         label="ELO multiplier on coins" />
          </div>
          <div className="help-card-body">
            <p>
              A word and its definition are shown on screen. Choose the option that
              means the same as the target word. Questions are drawn from your ELO
              tier so difficulty grows as your rating rises.
            </p>
            <Steps steps={[
              "Read the word and its definition shown on the card.",
              "Select the option (A–D) that best means the same as the target word.",
              "Correct answers earn +1 ELO; wrong answers cost −3 ELO.",
              "End the session at any time to save your ELO and collect coins.",
            ]} />
            <p>
              <strong>Scoring</strong> — coins are calculated as{" "}
              <strong>correct answers × (ELO ÷ 1000)</strong>, so higher-ranked
              players earn more per session. A speaker button lets you hear the
              question and all options read aloud.
            </p>
            <Tips items={[
              "Use the speaker icon if you're unsure how a word sounds — hearing it can help.",
              "A streak of 3+ correct answers in a row shows a 🔥 streak indicator — aim to keep it going.",
              "Words are enriched in the background as you play, so the question pool grows over time.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            7. ANTONYMS
        ════════════════════════════════════════ */}
        <div className="help-card" id="antonyms">
          <div className="help-card-header">
            <div className="help-card-icon orange"><ArrowLeftRight size={22} /></div>
            <div>
              <p className="help-card-title">Antonyms</p>
              <p className="help-card-subtitle">Vocabulary · Continuous rounds</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<ArrowLeftRight size={13} />} label="4 options per question" />
            <Stat icon={<TrendingUp size={13} />}     label="ELO-scaled difficulty" />
            <Stat icon={<Coins size={13} />}           label="ELO multiplier on coins" />
          </div>
          <div className="help-card-body">
            <p>
              The same format as Synonyms, but now you must pick the word that means
              the <em>opposite</em> of the target word. Only words with genuine
              antonyms appear as questions — concrete nouns with no true opposites are
              never asked.
            </p>
            <Steps steps={[
              "Read the word and its definition shown on the card.",
              "Select the option (A–D) that best means the opposite of the target word.",
              "Correct answers earn +1 ELO; wrong answers cost −3 ELO.",
              "End the session at any time to save your ELO and collect coins.",
            ]} />
            <p>
              <strong>Scoring</strong> — same ELO multiplier coin formula as
              Synonyms. Both games share the same ELO rating, so playing either
              counts toward the same rank.
            </p>
            <Tips items={[
              "Antonyms are often harder than synonyms — take an extra moment to think before selecting.",
              "Use the speaker button to hear the question read aloud if you need a moment to process.",
              "If no antonyms exist for a word (e.g. concrete nouns), that word will never appear as a question.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            8. RANKED MODE
        ════════════════════════════════════════ */}
        <div className="help-card" id="ranked">
          <div className="help-card-header">
            <div className="help-card-icon red"><Flame size={22} /></div>
            <div>
              <p className="help-card-title">Ranked Mode</p>
              <p className="help-card-subtitle">Competitive · 15 minutes</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Clock size={13} />}       label="15 minutes" />
            <Stat icon={<ListOrdered size={13} />} label="6 MCQ + 4 written (20 marks)" />
            <Stat icon={<TrendingUp size={13} />}  label="ELO ladder" />
            <Stat icon={<Award size={13} />}       label="Novice → Legend" />
          </div>
          <div className="help-card-body">
            <p>
              Ranked Mode is the competitive centrepiece of Owllet Reading Quest.
              Your <strong>ELO rating</strong> determines the difficulty of each
              session — the higher your ELO, the harder the questions. Every game
              either raises or lowers your rating depending on performance.
            </p>

            <Steps steps={[
              "Enter Ranked Mode — questions are automatically matched to your current ELO tier.",
              "Answer 6 MCQ questions (1 mark each) and 4 written questions (variable marks, 20 marks total).",
              "Submit before the 15-minute timer expires.",
              "Wait for AI marking on written answers, then view your ELO change.",
            ]} />

            <p>
              <strong>ELO changes</strong> range from <strong>−30</strong> (0%
              score) to <strong>+50</strong> (100% score). Your ELO tier is your
              score ÷ 100, so each 100 ELO points is one tier of difficulty.
            </p>

            <div className="help-table-wrap">
              <table className="help-score-table">
                <thead>
                  <tr><th>Rank Title</th><th>ELO Range</th></tr>
                </thead>
                <tbody>
                  <tr><td>Novice</td><td>0 – 799</td></tr>
                  <tr><td>Apprentice</td><td>800 – 1099</td></tr>
                  <tr><td>Scholar</td><td>1100 – 1399</td></tr>
                  <tr><td>Advanced</td><td>1400 – 1699</td></tr>
                  <tr><td>Expert</td><td>1700 – 1999</td></tr>
                  <tr><td>Master</td><td>2000 – 2399</td></tr>
                  <tr><td>Legend</td><td>2400+</td></tr>
                </tbody>
              </table>
            </div>

            <Tips items={[
              "Written answers carry 14 of the 20 marks — strong writing is the fastest way to climb.",
              "Consistent 60–70% scores will steadily grow your ELO over many sessions.",
              "Check the Statistics page to track your ELO history and identify weak areas.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            9. PREFERENCES
        ════════════════════════════════════════ */}
        <div className="help-card" id="preferences">
          <div className="help-card-header">
            <div className="help-card-icon violet"><Settings size={22} /></div>
            <div>
              <p className="help-card-title">Preferences</p>
              <p className="help-card-subtitle">Account · Settings icon in the navigation bar</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<ListOrdered size={13} />}      label="Tiers 1–21" />
            <Stat icon={<SlidersHorizontal size={13} />} label="ELO 0–250 per game" />
            <Stat icon={<Zap size={13} />}               label="Applies immediately on save" />
          </div>
          <div className="help-card-body">
            <p>
              The Preferences panel is accessed via the <strong>⚙ settings icon</strong> in
              the top navigation bar. It lets you personalise two aspects of your game
              experience: your default difficulty tier and your vocabulary ELO starting
              point for each vocabulary game.
            </p>

            {/* ── Difficulty Tier ── */}
            <div className="help-pref-block">
              <p className="help-pref-heading">
                <ListOrdered size={14} /> Default Difficulty Tier
              </p>
              <p>
                The tier dropdown lets you choose a difficulty level from{" "}
                <strong>Tier 1</strong> (easiest) to <strong>Tier 21 — A-Level</strong>{" "}
                (hardest). Once set, this tier is pre-selected on every game setup screen
                so you don't have to pick it each session. Users in the under-16 age
                group can select up to Tier 17.
              </p>
              <p>
                Setting a default tier does not lock you in — you can still override it
                on any individual game's setup screen before starting.
              </p>
            </div>

            {/* ── Vocabulary ELO ── */}
            <div className="help-pref-block">
              <p className="help-pref-heading">
                <SlidersHorizontal size={14} /> Vocabulary ELO Adjustment
              </p>
              <p>
                Three sliders (range <strong>0–250</strong>) let you manually set the
                ELO for each vocabulary game. The ELO controls which words you are
                shown — a higher ELO pulls harder, less common words from the pool; a
                lower ELO keeps questions at a simpler, more familiar level.
              </p>
              <div className="help-elo-pills">
                <span className="help-elo-pill teal"><Repeat2 size={13} /> Synonyms</span>
                <span className="help-elo-pill orange"><ArrowLeftRight size={13} /> Antonyms</span>
                <span className="help-elo-pill green"><Star size={13} /> Vocab Matching</span>
              </div>
              <div className="help-elo-warning">
                <strong>⚠ Warning:</strong> Applying a changed ELO slider immediately
                overwrites your current ELO score for that game. Because ELO controls
                word complexity, lowering it makes the game easier right away; raising
                it increases difficulty immediately. The warning banner only appears in
                the Preferences panel when you have moved a slider from its current value.
              </div>
            </div>

            <Steps steps={[
              "Click the ⚙ settings icon in the top navigation bar.",
              "Choose a Default Difficulty Tier from the dropdown (optional — leave blank to keep picking manually).",
              "Drag any ELO slider to adjust the starting difficulty for that vocabulary game.",
              "Click Apply — all changes take effect immediately and your Owllet ID is shown at the top of the panel.",
            ]} />

            <Tips items={[
              "Use the difficulty tier preset if you always play at the same level to avoid repetitive setup.",
              "If vocabulary games feel too easy after a break, raise the ELO slider to re-challenge yourself.",
              "If vocabulary games feel too hard, lower the ELO slider to recalibrate — then work your way back up naturally.",
              "Your Owllet ID is also displayed at the top of the Preferences panel for quick reference.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            10. THEME SELECTOR
        ════════════════════════════════════════ */}
        <div className="help-card" id="theme">
          <div className="help-card-header">
            <div className="help-card-icon indigo"><Palette size={22} /></div>
            <div>
              <p className="help-card-title">Theme Selector</p>
              <p className="help-card-subtitle">Appearance · Palette icon in the navigation bar</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Palette size={13} />}  label="21 themes available" />
            <Stat icon={<Monitor size={13} />}  label="Applies instantly" />
            <Stat icon={<Zap size={13} />}       label="Saved to your account" />
          </div>
          <div className="help-card-body">
            <p>
              The <strong>palette icon (🎨)</strong> in the top navigation bar opens
              the Theme Selector. Choosing a theme changes the colour palette of the
              entire site — buttons, gradients, badges, and backgrounds all update
              instantly. The site must be dark on all other browsers or devices until
              you sign in there, because the theme is tied to your account.
            </p>

            <div className="help-pref-block">
              <p className="help-pref-heading">
                <Palette size={14} /> How it works
              </p>
              <p>
                Clicking a colour swatch applies the theme immediately — no page reload
                needed. When you are signed in, the chosen theme is saved to your
                account so it loads automatically the next time you log in on any
                device. When you are not signed in, the theme defaults to{" "}
                <strong>Midnight</strong> and any manual changes are browser-local only
                (they are not persisted after you log in or out).
              </p>
            </div>

            <div className="help-pref-block">
              <p className="help-pref-heading">
                <Monitor size={14} /> Available themes
              </p>
              <div className="help-theme-grid">
                {[
                  "Violet","Ocean","Forest","Sunset","Rose","Midnight",
                  "Sky","Lavender","Teal","Amber","Fuchsia","Coral",
                  "Mint","Peach","Periwinkle","Candy","Powder","Blossom",
                  "Butter","Deep Teal","Jade",
                ].map((t) => (
                  <span key={t} className="help-theme-badge">{t}</span>
                ))}
              </div>
            </div>

            <Steps steps={[
              "Sign in to your account — the Theme Selector is only available when logged in.",
              "Click the palette icon (🎨) in the top navigation bar.",
              "Click any colour swatch to apply that theme instantly.",
              "The theme is saved automatically — it will reload on your next login.",
            ]} />

            <Tips items={[
              "Midnight is the default theme for all new accounts and the anonymous browsing state.",
              "If you log out, the theme resets to Midnight until you log back in.",
              "Try different themes across sessions — your choice is always saved and can be changed at any time.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            11. MY RESULTS
        ════════════════════════════════════════ */}
        <div className="help-card" id="my-results">
          <div className="help-card-header">
            <div className="help-card-icon green"><ClipboardList size={22} /></div>
            <div>
              <p className="help-card-title">My Results</p>
              <p className="help-card-subtitle">Progress · View and print past game results</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<ClipboardList size={13} />} label="All scored game types" />
            <Stat icon={<Target size={13} />}         label="Filterable by game mode" />
            <Stat icon={<FileText size={13} />}       label="Printable result sheets" />
          </div>
          <div className="help-card-body">
            <p>
              My Results shows your past completed sessions for all scored game
              modes — Comprehension, MCQ Quiz, Ranked Mode, and Book Comprehension.
              Each entry shows your score, date, and game type at a glance.
            </p>
            <Steps steps={[
              "Navigate to My Results from your profile or the home screen.",
              "Use the filter dropdown to narrow results by game type.",
              "Click any result row to open the full detail view with questions, your answers, and the mark scheme.",
              "Use the print button on a detail page to generate a printable copy of that result.",
            ]} />
            <p>
              Result sheets can be printed as a formatted page — useful for reviewing
              written answers or sharing progress with a teacher.
            </p>
            <Tips items={[
              "Filter by Ranked Mode to review your ELO sessions over time.",
              "Printing a Comprehension result gives you the full mark scheme alongside your written answers.",
              "Results are stored indefinitely — you can revisit any past session.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            12. MY STATS
        ════════════════════════════════════════ */}
        <div className="help-card" id="my-stats">
          <div className="help-card-header">
            <div className="help-card-icon indigo"><BarChart2 size={22} /></div>
            <div>
              <p className="help-card-title">My Stats</p>
              <p className="help-card-subtitle">Progress · Statistics and ELO history charts</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<TrendingUp size={13} />} label="ELO history charts" />
            <Stat icon={<Zap size={13} />}         label="XP &amp; level bar" />
            <Stat icon={<BarChart2 size={13} />}   label="Activity by game type" />
          </div>
          <div className="help-card-body">
            <p>
              The Statistics page gives you a visual overview of your growth across
              the platform. ELO history is plotted as a line chart for each rated
              game — Ranked, Vocab Matching, Synonyms, and Antonyms — so you can
              see your trajectory at a glance.
            </p>
            <Steps steps={[
              "Navigate to Statistics from the home screen.",
              "View your XP bar showing your current level and progress to the next.",
              "Scroll to the ELO charts to track rating changes over time for each vocabulary and ranked game.",
              "Check the activity breakdown to see which game types you play most.",
              "Review recent sessions at the bottom for a quick overview of your latest activity.",
            ]} />
            <Tips items={[
              "A flat ELO line means consistent performance — look for the sessions where it jumps or dips to learn from them.",
              "The XP bar updates after every game — keep playing to level up.",
              "Switch between ELO charts to compare your progress across Synonyms, Antonyms, and Ranked.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            13. MY REPORT
        ════════════════════════════════════════ */}
        <div className="help-card" id="my-report">
          <div className="help-card-header">
            <div className="help-card-icon teal"><User size={22} /></div>
            <div>
              <p className="help-card-title">My Report</p>
              <p className="help-card-subtitle">Progress · Personal performance dashboard</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<ListOrdered size={13} />}    label="Total plays summary" />
            <Stat icon={<Clock size={13} />}           label="Last 30 sessions" />
            <Stat icon={<Star size={13} />}            label="Vocabulary word stats" />
          </div>
          <div className="help-card-body">
            <p>
              My Report is your personal performance dashboard. It summarises your
              activity across every game mode and gives you a detailed breakdown of
              your vocabulary word results — how many words you've seen, answered
              correctly, and got wrong.
            </p>
            <Steps steps={[
              "Navigate to My Report from the home screen.",
              "View the summary cards for total plays, game plays, vocabulary tests, and last active date.",
              "Scroll to the activity breakdown to see how your plays are distributed across game types.",
              "Check the recent sessions table to review your last 30 completed games.",
              "Scroll to the vocabulary section to see per-word correct and incorrect counts.",
            ]} />
            <Tips items={[
              "Use the vocabulary word stats to find words you consistently get wrong — focus your study there.",
              "The game type breakdown shows if you're neglecting any mode — variety helps overall progress.",
              "Recent sessions show scores, so you can quickly spot which sessions went well or poorly.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            14. LOGIN CALENDAR
        ════════════════════════════════════════ */}
        <div className="help-card" id="calendar">
          <div className="help-card-header">
            <div className="help-card-icon orange"><CalendarDays size={22} /></div>
            <div>
              <p className="help-card-title">Login Calendar</p>
              <p className="help-card-subtitle">Rewards · Daily check-in bonus coins</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Coins size={13} />}      label="Coins for daily check-in" />
            <Stat icon={<Flame size={13} />}       label="Streak multiplier bonus" />
            <Stat icon={<CalendarDays size={13} />} label="One check-in per day" />
          </div>
          <div className="help-card-body">
            <p>
              The Login Calendar rewards you with coins every day you visit the site.
              Log in and click the calendar to claim your daily bonus — the longer
              your streak, the more coins you earn each day.
            </p>

            <div className="help-pref-block">
              <p className="help-pref-heading">
                <Coins size={14} /> Streak bonus formula
              </p>
              <p>
                Daily coins are calculated as{" "}
                <strong>10 + 20 × ln(streak + 1)</strong>, so the bonus grows
                quickly at first and then levels off. At a 1-day streak you earn
                around <strong>23 coins</strong>; at 7 days around{" "}
                <strong>51 coins</strong>; at 30 days around{" "}
                <strong>78 coins</strong>. Missing a day resets your streak to zero.
              </p>
            </div>

            <Steps steps={[
              "Click Calendar in the navigation bar.",
              "Click the Check In button to claim your daily bonus.",
              "Your streak counter and coin reward are shown on the calendar page.",
              "Return the next day to extend your streak and earn a larger bonus.",
            ]} />
            <Tips items={[
              "You only need to visit the calendar once per day — you don't need to play a game.",
              "Streak milestones at 1, 2, 5, 9, 15, and 30 days show celebratory messages.",
              "Use the coins you collect on the Owl Hatchery to roll for new owl companions.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            15. OWL HATCHERY
        ════════════════════════════════════════ */}
        <div className="help-card" id="owl-hatchery">
          <div className="help-card-header">
            <div className="help-card-icon violet"><Gift size={22} /></div>
            <div>
              <p className="help-card-title">Owl Hatchery</p>
              <p className="help-card-subtitle">Rewards · Spend coins to hatch owl companions</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Coins size={13} />} label="200 coins per roll" />
            <Stat icon={<Gift size={13} />}   label="1×, 10×, or 100× rolls" />
            <Stat icon={<Star size={13} />}   label="6 rarity tiers" />
          </div>
          <div className="help-card-body">
            <p>
              The Owl Hatchery (Gacha) lets you spend coins to hatch unique owl
              companions. Each roll gives you a randomly drawn owl from one of six
              rarity tiers. Multi-rolls come with a significant coin discount.
            </p>

            <div className="help-table-wrap">
              <table className="help-score-table">
                <thead>
                  <tr><th>Roll type</th><th>Cost</th><th>Discount</th></tr>
                </thead>
                <tbody>
                  <tr><td>Single roll</td><td>200 coins</td><td>—</td></tr>
                  <tr><td>10-roll</td><td>1,500 coins</td><td>25% off</td></tr>
                  <tr><td>100-roll</td><td>10,000 coins</td><td>50% off</td></tr>
                </tbody>
              </table>
            </div>

            <div className="help-table-wrap">
              <table className="help-score-table">
                <thead>
                  <tr><th>Rarity</th><th>Chance</th></tr>
                </thead>
                <tbody>
                  <tr><td>Common</td><td>50%</td></tr>
                  <tr><td>Uncommon</td><td>35%</td></tr>
                  <tr><td>Rare</td><td>10%</td></tr>
                  <tr><td>Epic</td><td>3.5%</td></tr>
                  <tr><td>Legendary</td><td>1%</td></tr>
                  <tr><td>Mythical</td><td>0.5%</td></tr>
                </tbody>
              </table>
            </div>

            <Steps steps={[
              "Navigate to the Owl Hatchery from the home screen.",
              "Choose a roll type — single, 10-roll, or 100-roll.",
              "Watch the hatch animation reveal your owls.",
              "Keep the owl to add it to your collection, or discard it to receive a partial coin refund.",
            ]} />
            <Tips items={[
              "The 100-roll is the best value — 50% off means 5 coins per roll on average.",
              "Discarding an owl returns some coins — don't feel stuck with duplicates.",
              "Earn coins from the Login Calendar and game sessions to fund your rolls.",
            ]} />
          </div>
        </div>

        {/* ════════════════════════════════════════
            16. MY COLLECTION
        ════════════════════════════════════════ */}
        <div className="help-card" id="my-collection">
          <div className="help-card-header">
            <div className="help-card-icon red"><Heart size={22} /></div>
            <div>
              <p className="help-card-title">My Collection</p>
              <p className="help-card-subtitle">Rewards · Owl Nest and favourites display</p>
            </div>
          </div>
          <div className="help-stats-row">
            <Stat icon={<Heart size={13} />}   label="All owned owls" />
            <Stat icon={<Star size={13} />}     label="Grouped by rarity" />
            <Stat icon={<Award size={13} />}    label="6-slot High Nest display" />
          </div>
          <div className="help-card-body">
            <p>
              My Collection (the Owl Nest) shows every owl you have hatched,
              organised by rarity tier — Common through Mythical. You can choose
              six favourite owls to place in the <strong>High Nest</strong>, a
              premium display that sits at the top of your nest.
            </p>
            <Steps steps={[
              "Navigate to My Collection from the home screen.",
              "Browse your owls, grouped by rarity.",
              "Click an owl to view its details and set it as a High Nest favourite.",
              "Your six High Nest slots are shown prominently at the top of the page.",
            ]} />
            <p>
              Some owl slots have a <strong>cooldown timer</strong> — these owls
              are temporarily unavailable after being placed or removed from the
              High Nest. The timer is shown on the card.
            </p>
            <Tips items={[
              "Hatch more owls from the Owl Hatchery to fill out your collection.",
              "Rarer owls have more detailed artwork — keep an eye out for Mythical drops.",
              "Use the High Nest to show off your most prized owls at the top of your collection.",
            ]} />
          </div>
        </div>

      </div>
    </div>
  );
}
